import { useDataProvider } from "react-admin";
import { useQuery } from "react-query";
import { Store } from "./store";
import { debug_log } from "./debugLog";

function shortHash(obj) {
  const str = JSON.stringify(obj);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0; // simple 32-bit hash
  }
  return hash.toString(36); // base36 → shorter (letters+digits)
}

/**
 * Replacement for react-admin useGetOne that uses useQuery under the hood,
 * so query options such as "enabled" work
 * https://tanstack.com/query/v3/docs/framework/react/reference/useQuery
 *
 * @param {*} resource
 * @param {*} params
 * @param {*} options
 * @returns
 */
export const useQueryRaw = (method, resource, params, options) => {
  const dataProvider = useDataProvider();
  const queryKey = [resource, params.id || shortHash(params), method];

  return useQuery(
    queryKey,
    () => dataProvider[method](resource, params),
    options
  );
};

const cachedOptions = {
  cacheTimeout: 5 * 60,
  refetchOnMount: false,
};

export const useQueryCached = (method, resource, params = {}, options = {}) => {
  const paramsHash = params.id || shortHash(params);
  const now = Math.floor(Date.now() / 1000);
  options = {
    ...cachedOptions,
    ...options,
  };

  const requestKey = `${method}.${resource}.${paramsHash}`;
  const cacheKey = `RaStore.requestCache.${requestKey}`;
  const requestCache = Store.get(cacheKey) || {};

  const cacheAge = now - requestCache.timestamp;
  const shouldFetch = !(requestCache.data && cacheAge < options.cacheTimeout);

  if (options.enabled === undefined) {
    options.enabled = shouldFetch;
  } else {
    options.enabled = options.enabled && shouldFetch;
  }

  if (options.enabled) {
    debug_log(`useQueryCached: Requesting ${requestKey}...`, 5);
  } else {
    options.initialData = requestCache.data;
    debug_log(
      `useQueryCached: Skipping ${requestKey} (TTL ${
        options.cacheTimeout - cacheAge
      } sec)...`,
      5
    );
  }

  return useQueryRaw(method, resource, params, {
    ...options,
    onSuccess: (data) => {
      debug_log(`useQueryCached: Response for ${requestKey}...`, 5);
      if (data) {
        Store.set(cacheKey, {
          data,
          timestamp: now,
          query: { method, resource, params },
        });
      }
    },
  });
};
